import React, { useState, useEffect, useCallback, useRef } from "react";
import threadApi from "../../api/threadApi";
import MessageItem from "./MessageItem";
import VirtualizedMessageList from "./VirtualizedMessageList";

/**
 * MessageList Component
 *
 * Displays a scrollable list of messages with pagination support.
 * Handles loading states, infinite scroll, and message fetching.
 * Can optionally use virtualization for performance with large lists.
 * Requirements: 3.2, 7.1, 8.5
 *
 * @param {Object} props - Component props
 * @param {string} props.threadId - Thread ID to fetch messages for
 * @param {Array} props.messages - Messages array (if provided, disables auto-loading)
 * @param {boolean} props.loading - Loading state from parent
 * @param {Function} props.onLoadMore - Callback for loading more messages
 * @param {Function} props.onMessageUpdate - Callback when messages are updated
 * @param {Object} props.currentUser - Current user data for interaction states
 * @param {string} props.highlightMessageId - Message ID to highlight (from notifications)
 * @param {string} props.highlightReplyId - Reply ID to highlight (from notifications)
 * @param {boolean} props.disableAutoLoad - Disable automatic message loading
 * @param {boolean} props.useVirtualization - Enable virtual scrolling for performance
 * @param {number} props.virtualizationThreshold - Minimum messages to enable virtualization
 * @param {number} props.containerHeight - Height for virtualized container
 * @param {string} props.className - Additional CSS classes
 */
const MessageList = ({
  threadId,
  messages: propMessages,
  loading: propLoading,
  onLoadMore,
  onMessageUpdate,
  currentUser,
  highlightMessageId,
  highlightReplyId,
  disableAutoLoad = false,
  useVirtualization = false,
  virtualizationThreshold = 50,
  containerHeight = 600,
  className = "",
}) => {
  // Determine if we should use prop messages or load our own
  const usePropsMessages = propMessages && Array.isArray(propMessages);
  const shouldAutoLoad = !usePropsMessages && !disableAutoLoad;

  // State management
  const [state, setState] = useState({
    messages: usePropsMessages ? propMessages : [],
    loading: usePropsMessages ? propLoading || false : false,
    loadingMore: false,
    error: null,
    pagination: {
      page: 1,
      hasMore: false,
      total: 0,
      totalPages: 0,
    },
    initialized: usePropsMessages,
  });

  // Refs for scroll management
  const listRef = useRef(null);
  const loadMoreRef = useRef(null);
  const isLoadingRef = useRef(false);

  // Determine if we should use virtualization
  const shouldUseVirtualization =
    useVirtualization && state.messages.length >= virtualizationThreshold;

  /**
   * Auto-scroll to highlighted message when messages load
   */
  useEffect(() => {
    if (highlightMessageId && state.messages.length > 0 && state.initialized) {
      // Small delay to ensure DOM is updated
      const timer = setTimeout(() => {
        const messageElement = document.getElementById(
          `message-${highlightMessageId}`
        );
        if (messageElement) {
          messageElement.scrollIntoView({
            behavior: "smooth",
            block: "center",
          });
        }
      }, 100);

      return () => clearTimeout(timer);
    }
  }, [highlightMessageId, state.messages.length, state.initialized]);

  /**
   * Update state with new data
   */
  const updateState = useCallback((updates) => {
    setState((prevState) => ({
      ...prevState,
      ...updates,
    }));
  }, []);

  /**
   * Load messages for the current page (only if not using prop messages)
   */
  const loadMessages = useCallback(
    async (page = 1, append = false) => {
      if (!threadId || isLoadingRef.current || usePropsMessages) {
        return;
      }

      isLoadingRef.current = true;

      try {
        // Set loading state
        updateState({
          loading: !append,
          loadingMore: append,
          error: null,
        });

        // Use larger page size for virtualization to reduce API calls
        const pageSize = shouldUseVirtualization ? 25 : 10;

        // Fetch messages from API
        const response = await threadApi.getMessages(threadId, page, pageSize);

        // Update state with new messages
        const newMessages = append
          ? [...state.messages, ...response.messages]
          : response.messages;

        updateState({
          messages: newMessages,
          pagination: {
            page: response.pagination.page,
            hasMore: response.pagination.hasMore,
            total: response.pagination.total,
            totalPages: response.pagination.totalPages,
          },
          loading: false,
          loadingMore: false,
          initialized: true,
        });

        // Notify parent of message update
        if (onMessageUpdate) {
          onMessageUpdate({
            messages: response.messages,
            pagination: response.pagination,
            append,
          });
        }
      } catch (error) {
        console.error("Failed to load messages:", error);

        updateState({
          loading: false,
          loadingMore: false,
          error: error.message || "Failed to load messages",
        });
      } finally {
        isLoadingRef.current = false;
      }
    },
    [
      threadId,
      onMessageUpdate,
      updateState,
      usePropsMessages,
      state.messages,
      shouldUseVirtualization,
    ]
  );

  /**
   * Load more messages (next page) - only if not using prop messages
   */
  const loadMoreMessages = useCallback(() => {
    if (usePropsMessages) {
      // Delegate to parent
      onLoadMore?.();
      return;
    }

    if (state.pagination.hasMore && !isLoadingRef.current) {
      loadMessages(state.pagination.page + 1, true);
    }
  }, [
    usePropsMessages,
    onLoadMore,
    state.pagination.hasMore,
    state.pagination.page,
    loadMessages,
  ]);

  /**
   * Refresh messages (reload first page) - only if not using prop messages
   */
  const refreshMessages = useCallback(() => {
    if (usePropsMessages) {
      // Cannot refresh when using prop messages
      return;
    }

    if (!isLoadingRef.current) {
      loadMessages(1, false);
    }
  }, [usePropsMessages, loadMessages]);

  /**
   * Handle message interactions (like, reply, etc.)
   */
  const handleMessageInteraction = useCallback(
    (messageId, action, data) => {
      // Update local message state optimistically
      setState((prevState) => ({
        ...prevState,
        messages: prevState.messages.map((message) => {
          if (message._id === messageId) {
            switch (action) {
              case "like":
                return {
                  ...message,
                  hasUserLiked: data.isLiked,
                  isLiked: data.isLiked, // Keep both for compatibility
                  likeCount: data.likeCount,
                };
              case "replyPosted":
                return {
                  ...message,
                  replyCount: data.replyCount,
                };
              default:
                return message;
            }
          }
          return message;
        }),
      }));

      // Notify parent of interaction
      if (onMessageUpdate) {
        onMessageUpdate({
          action,
          messageId,
          data,
        });
      }
    },
    [onMessageUpdate]
  );

  /**
   * Add new message to the list (for real-time updates)
   * Messages are added at the top since they're sorted newest first
   */
  const addMessage = useCallback((newMessage) => {
    setState((prevState) => ({
      ...prevState,
      messages: [newMessage, ...prevState.messages], // Add to beginning (newest first)
      pagination: {
        ...prevState.pagination,
        total: prevState.pagination.total + 1,
      },
    }));

    // Scroll to the new message at the top for better UX
    setTimeout(() => {
      if (listRef.current) {
        listRef.current.scrollTo({
          top: 0,
          behavior: "smooth",
        });
      }
    }, 100);
  }, []);

  /**
   * Intersection Observer for infinite scroll
   */
  useEffect(() => {
    if (!loadMoreRef.current || !state.pagination.hasMore) {
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (entry.isIntersecting && !isLoadingRef.current) {
          loadMoreMessages();
        }
      },
      {
        root: null,
        rootMargin: "100px",
        threshold: 0.1,
      }
    );

    observer.observe(loadMoreRef.current);

    return () => {
      observer.disconnect();
    };
  }, [state.pagination.hasMore, loadMoreMessages]);

  /**
   * Load initial messages when threadId changes (only if should auto-load)
   */
  useEffect(() => {
    if (threadId && !state.initialized && shouldAutoLoad) {
      loadMessages(1, false);
    }
  }, [threadId, state.initialized, shouldAutoLoad, loadMessages]);

  /**
   * Update messages when prop messages change
   */
  useEffect(() => {
    if (usePropsMessages) {
      updateState({
        messages: propMessages,
        loading: propLoading || false,
        initialized: true,
        error: null,
        // Use a reasonable pagination state when using props
        pagination: {
          page: 1,
          hasMore: false,
          total: propMessages.length, // Use actual message count from props
          totalPages: 1,
        },
      });
    }
  }, [usePropsMessages, propMessages, propLoading, updateState]);

  /**
   * Expose methods to parent component
   */
  useEffect(() => {
    if (onMessageUpdate) {
      onMessageUpdate({
        refreshMessages,
        addMessage,
        loadMoreMessages,
      });
    }
  }, [onMessageUpdate, refreshMessages, addMessage, loadMoreMessages]);

  // If using virtualization and we have enough messages, use VirtualizedMessageList
  if (shouldUseVirtualization) {
    return (
      <VirtualizedMessageList
        messages={state.messages}
        loading={state.loading || state.loadingMore}
        hasMore={state.pagination.hasMore}
        onLoadMore={loadMoreMessages}
        onLikeMessage={(messageId, isLiked, likeCount) =>
          handleMessageInteraction(messageId, "like", { isLiked, likeCount })
        }
        onReplyToMessage={(messageId, replyCount) =>
          handleMessageInteraction(messageId, "replyPosted", { replyCount })
        }
        currentUserId={currentUser?._id}
        highlightMessageId={highlightMessageId}
        highlightReplyId={highlightReplyId}
        containerHeight={containerHeight}
        className={className}
      />
    );
  }

  // Render loading state
  if (state.loading && !state.initialized) {
    return (
      <div className={`flex justify-center items-center py-8 ${className}`}>
        <div className="flex flex-col items-center space-y-3">
          <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-deep-red"></div>
          <span className="text-sm text-medium-grey">Loading messages...</span>
        </div>
      </div>
    );
  }

  // Render error state
  if (state.error && !state.initialized) {
    return (
      <div className={`text-center py-8 ${className}`}>
        <div className="text-red-600 mb-4">
          <svg
            className="w-12 h-12 mx-auto mb-2"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <p className="text-lg font-medium">Failed to load messages</p>
          <p className="text-sm text-gray-500 mt-1">{state.error}</p>
        </div>
        <button
          onClick={refreshMessages}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          disabled={isLoadingRef.current}
        >
          Try Again
        </button>
      </div>
    );
  }

  // Render empty state
  if (state.messages.length === 0 && state.initialized) {
    return (
      <div className={`text-center py-12 ${className}`}>
        <div className="text-gray-500">
          <svg
            className="w-16 h-16 mx-auto mb-4 text-gray-300"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={1.5}
              d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
            />
          </svg>
          <p className="text-lg font-medium text-gray-600">No messages yet</p>
          <p className="text-sm text-gray-500 mt-1">
            Be the first to start the discussion!
          </p>
        </div>
      </div>
    );
  }

  // Render message list
  return (
    <div className={`message-list ${className}`} ref={listRef}>
      {/* Load Older Messages Button - At the top for newest-first order */}
      {state.pagination.hasMore && state.initialized && (
        <div className="load-older-container py-4 border-b border-gray-100">
          <div className="text-center">
            <button
              onClick={loadMoreMessages}
              disabled={state.loadingMore}
              className="px-4 py-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors disabled:opacity-50 text-sm font-medium"
            >
              {state.loadingMore ? (
                <span className="flex items-center space-x-2">
                  <svg
                    className="w-4 h-4 animate-spin"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  <span>Loading older messages...</span>
                </span>
              ) : (
                `Load older messages (${
                  state.pagination.total - state.messages.length
                } remaining)`
              )}
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="space-y-4">
        {state.messages.map((message, index) => (
          <MessageItem
            key={message._id}
            message={message}
            currentUser={currentUser}
            onInteraction={handleMessageInteraction}
            isFirst={index === 0}
            isLast={index === state.messages.length - 1}
            isHighlighted={highlightMessageId === message._id}
            highlightReplyId={highlightReplyId}
          />
        ))}
      </div>

      {/* Pagination info */}
      {state.initialized && state.messages.length > 0 && (
        <div className="text-center py-4 text-sm text-gray-500 border-t">
          Showing {state.messages.length} of {state.pagination.total} messages
          {state.pagination.totalPages > 1 && (
            <span className="ml-2">
              (Page {state.pagination.page} of {state.pagination.totalPages})
            </span>
          )}
        </div>
      )}

      {/* Error overlay for load more failures */}
      {state.error && state.initialized && (
        <div className="text-center py-2">
          <p className="text-red-600 text-sm mb-2">{state.error}</p>
          <button
            onClick={refreshMessages}
            className="text-blue-600 hover:text-blue-800 text-sm underline"
          >
            Refresh messages
          </button>
        </div>
      )}
    </div>
  );
};

export default MessageList;
